import {
  ProviderUnavailableError,
  type ProviderCallOptions,
  type ProviderJobHandle,
  type ProviderVideoJobState,
  type VideoGenRequest,
  type VideoProvider,
} from "@loumy/contracts";

// §7 `video`-Provider → fal.ai-Aggregator (D57). Bezieht Pixverse-Modelle über die
// fal.ai-Queue-API statt der Pixverse-Direkt-API (1 Key, kein separater Pixverse-Vertrag).
// Pixverse-Direkt-Adapter (packages/providers/src/pixverse.ts, D55) bleibt registrierbarer
// Swap. ECHTER Adapter, ENV-gated (ohne FAL_API_KEY → ProviderUnavailableError, §13).
//
// Dokumentierter Contract (fal.ai/docs/model-endpoints/queue + Modell-Seite, verifiziert
// 2026-06-01):
//   Auth   : Header `Authorization: Key <FAL_API_KEY>`.
//   Submit : POST {base}/{model}                       (JSON) → { request_id, status_url,
//                                                        response_url, cancel_url }
//   Status : GET  {status_url}  (= …/requests/{id}/status)  → { status: IN_QUEUE |
//                                                        IN_PROGRESS | COMPLETED, error? }
//   Result : GET  {response_url} (= …/requests/{id})        → { video: { url } }
//   Wir verwenden bewusst die VON fal ZURÜCKGEGEBENEN status_url/response_url (fal nutzt für
//   Status/Result den App-Prefix `fal-ai/pixverse`, NICHT den vollen Modell-Pfad — selbst
//   konstruieren ist fragil); beide werden gegen den fal-Host validiert (Defense-in-Depth).
//
// Modell-Contract `fal-ai/pixverse/v6/image-to-video` (verifiziert 2026-06-01):
//   image_url (REQUIRED, I2V-Start-Frame, öffentliche URL — R2-presigned via urlFor, D56),
//   prompt (REQUIRED), resolution ∈ 360p/540p/720p/1080p (Default 720p),
//   duration = INTEGER 1~15 s (Default 5 — wie Pixverse-V6-Direkt, D55; kein 5s-Floor),
//   generate_audio_switch (bool). Output: { video: { url, content_type, file_name, file_size } }.
//   Dauer-Semantik DECKT SICH mit dem Direkt-Adapter ⇒ sauberer Swap (gleiche Limits, D34):
//   sub-sekündige D49-Perioden (run 0.7) runden auf 1 s (D107: die FLF-Transition schließt
//   den Loop unabhängig von der exakten Periode).

const FAL_DEFAULT_BASE_URL = "https://queue.fal.run";
const FAL_DEFAULT_MODEL = "fal-ai/pixverse/v6/image-to-video";
// D76: Transition-Endpoint (first_image_url + end_image_url) für einen GESCHLOSSENEN Loop.
// Gleiches v6-Modell/derselbe Key, nur ein anderer Endpoint; Output-Form identisch ({video:{url}}).
const FAL_TRANSITION_MODEL = "fal-ai/pixverse/v6/transition";
const QUALITIES = new Set(["360p", "540p", "720p", "1080p"]);
const MIN_DURATION_S = 1;
const MAX_DURATION_S = 15; // v6 (1~15 s, ganzzahlig) — identisch zur Pixverse-Direkt-V6 (D55)

/** Klemmt eine angeforderte Dauer (s) auf den ganzzahligen v6-Bereich 1~15. */
export function clampFalDurationS(requestedS: number): number {
  return Math.max(MIN_DURATION_S, Math.min(MAX_DURATION_S, Math.round(requestedS)));
}

export interface FalConfig {
  apiKey?: string;
  baseUrl?: string;
  /** Optionaler Modell-Override (Default `fal-ai/pixverse/v6/image-to-video`). */
  model?: string;
  /** D76: Transition-Endpoint-Override (Default `fal-ai/pixverse/v6/transition`) — Loop=on. */
  transitionModel?: string;
}

/** Persistierte Poll-Koordinaten + Provenance-Bausteine (im Handle.raw, Resume-fähig D27). */
interface FalHandleRaw {
  statusUrl: string;
  responseUrl: string;
  model: string;
  resolution: string;
  duration: number;
  audio: boolean;
  seed: number | null;
}

export class FalVideoProvider implements VideoProvider {
  readonly id = "fal";
  readonly version = 1;
  readonly displayName = "fal.ai (Pixverse v6 I2V)";
  // v6-Limits (identisch zur Direkt-V6, D55). resolutions ASCENDING — der Default wird in
  // buildPipeline NAMENTLICH gewählt (nicht über die Array-Position, D34-Determinismus).
  readonly limits = {
    maxDurationS: MAX_DURATION_S,
    maxFps: 24,
    supportsAudio: true,
    resolutions: ["360p", "540p", "720p", "1080p"],
  };

  private readonly baseUrl: string;
  private readonly model: string;
  private readonly transitionModel: string;

  constructor(private readonly config: FalConfig = {}) {
    // Leere Env-Strings (`FAL_BASE_URL=`) als „nicht gesetzt" behandeln ⇒ Default greift.
    this.baseUrl = (config.baseUrl?.trim() || FAL_DEFAULT_BASE_URL).replace(/\/+$/, "");
    this.model = config.model?.trim() || FAL_DEFAULT_MODEL;
    this.transitionModel = config.transitionModel?.trim() || FAL_TRANSITION_MODEL;
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("video", this.id, "FAL_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  /** `Authorization: Key <key>` (fal-Contract). */
  private headers(apiKey: string, json: boolean): Record<string, string> {
    const h: Record<string, string> = { Authorization: `Key ${apiKey}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }

  /** fal-Queue-URLs validieren: müssen auf dem konfigurierten fal-Host liegen (SSRF/Drift). */
  private requireFalUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal submit: '${field}' fehlt in der Antwort.`);
    let host: string;
    try {
      host = new URL(url).origin;
    } catch {
      throw new Error(`fal submit: '${field}' ist keine gültige URL.`);
    }
    if (host !== new URL(this.baseUrl).origin)
      throw new Error(`fal submit: '${field}' (${host}) liegt nicht auf dem fal-Host (${new URL(this.baseUrl).origin}).`);
    return url;
  }

  async generate(req: VideoGenRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle> {
    const apiKey = this.requireKey();
    if (!req.startFrameUrl)
      throw new ProviderUnavailableError("video", this.id, "fal-Pixverse I2V braucht ein Start-Frame (startFrameUrl fehlt).");

    const resolution = req.resolution ?? "540p";
    if (!QUALITIES.has(resolution))
      throw new Error(`fal: unbekannte resolution '${resolution}' (∈ 360p/540p/720p/1080p).`);
    // v6 nimmt ganze Sekunden 1~15 — D49-Perioden (run 0.7) runden auf 1 s.
    const duration = clampFalDurationS(req.durationS);

    // D76/D107: Ist ein Endframe gesetzt, den TRANSITION-Endpoint nutzen — first_image_url +
    // end_image_url. loop=on: first=end=normalisiertes Bild ⇒ die Animation schließt sich auf
    // die Startpose (echter Loop, FLF); loop=off+End-Frame: gerichtete Transition (D91).
    // Sonst der reine i2v-Endpoint (image_url).
    const useTransition = typeof req.endFrameUrl === "string" && req.endFrameUrl.length > 0;
    const usedModel = useTransition ? this.transitionModel : this.model;
    const payload = useTransition
      ? {
          prompt: req.prompt,
          first_image_url: req.startFrameUrl,
          end_image_url: req.endFrameUrl,
          resolution,
          duration,
          generate_audio_switch: req.audio === true,
        }
      : {
          prompt: req.prompt,
          image_url: req.startFrameUrl,
          resolution,
          duration,
          generate_audio_switch: req.audio === true,
        };

    // Submit (D27): Handle VOR Persist sichern — der Runner persistiert das Rückgabe-Handle.
    const res = await fetch(`${this.baseUrl}/${usedModel}`, {
      method: "POST",
      headers: this.headers(apiKey, true),
      body: JSON.stringify(payload),
      signal: opts?.signal,
    });
    if (!res.ok) throw new Error(`fal submit: HTTP ${res.status} ${res.statusText}`);
    const body = (await res.json()) as { request_id?: string; status_url?: unknown; response_url?: unknown };
    if (!body.request_id) throw new Error("fal submit: keine request_id in der Antwort.");

    const raw: FalHandleRaw = {
      statusUrl: this.requireFalUrl(body.status_url, "status_url"),
      responseUrl: this.requireFalUrl(body.response_url, "response_url"),
      model: usedModel,
      resolution,
      duration,
      audio: req.audio === true,
      seed: null, // fal-Pixverse gibt keinen Seed zurück (Provenance, D39)
    };
    return { providerId: this.id, providerVersion: this.version, externalId: body.request_id, raw };
  }

  /**
   * GET im poll(): ok → Response; 429/5xx/Netzwerkabbruch → null (TRANSIENT → weiterpollen,
   * KEIN Re-generate — wichtig, da D27-Resume noch nicht verdrahtet ist, §13/M6); übrige 4xx
   * → terminaler Fehler (Contract/Auth, z. B. 401/404).
   */
  private async pollGet(url: string, apiKey: string, op: string, signal?: AbortSignal): Promise<Response | null> {
    let res: Response;
    try {
      res = await fetch(url, { method: "GET", headers: this.headers(apiKey, false), signal });
    } catch {
      return null; // Netzwerkabbruch / Abbruch → transient
    }
    if (res.ok) return res;
    if (res.status === 429 || res.status >= 500) return null; // transient
    throw new Error(`fal ${op}: HTTP ${res.status} ${res.statusText}`);
  }

  async poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderVideoJobState> {
    const apiKey = this.requireKey();
    const raw = (handle.raw ?? {}) as Partial<FalHandleRaw>;
    if (!raw.statusUrl || !raw.responseUrl)
      throw new Error("fal poll: Handle ohne status_url/response_url (kann nicht pollen).");
    // URLs AUCH im Resume-Pfad revalidieren (das Handle kann aus persistiertem, potenziell
    // manipuliertem Zustand kommen, D27) — verhindert SSRF/Key-Exfiltration an einen fremden
    // Host (generate() validiert nur beim ERSTEN Submit).
    const statusUrl = this.requireFalUrl(raw.statusUrl, "status_url");
    const responseUrl = this.requireFalUrl(raw.responseUrl, "response_url");

    const statusRes = await this.pollGet(statusUrl, apiKey, "status", opts?.signal);
    if (!statusRes) return { status: "processing" }; // transienter Fehler → weiterpollen
    const status = (await statusRes.json()) as { status?: string; error?: string; error_type?: string };

    if (status.error || status.error_type)
      return { status: "failed", error: `fal: ${status.error_type ?? "error"} ${status.error ?? ""}`.trim() };

    switch (status.status) {
      case "IN_QUEUE":
      case "IN_PROGRESS":
        return { status: "processing" };
      case "COMPLETED": {
        const resultRes = await this.pollGet(responseUrl, apiKey, "result", opts?.signal);
        if (!resultRes) return { status: "processing" }; // transient → Status bleibt COMPLETED
        const result = (await resultRes.json()) as { video?: { url?: string } };
        const url = result.video?.url;
        if (!url) return { status: "failed", error: "fal: COMPLETED, aber kein video.url im Ergebnis." };
        return {
          status: "done",
          output: {
            videoUrl: url,
            // fal-Pixverse bettet Audio in den Container (kein separater Track).
            audioUrl: null,
            provenance: {
              provider: this.id,
              modelVersion: raw.model ?? this.model,
              requestId: handle.externalId,
              seed: raw.seed ?? null,
            },
          },
        };
      }
      default:
        // Unbekannter/leerer Status → konservativ weiterpollen (wie Pixverse-Adapter).
        return { status: "processing" };
    }
  }
}
